/**
 * D-2 订单 —— `/api/orders`（契约 §7 D-2，2026-09-03 已建成）。
 *
 * ★ 契约硬约束：**只读 + 可取消，永不建单**。本层只有 list / detail / cancel。
 * ⚠ 取消只管「出票前撤单」，不是退票 —— 两者别在界面上做成同一个按钮（见 orders 契约）。
 * ⚠ 都要登录（网关「匿名禁止 `/api/orders/**`」），默认 `auth: 'required'`。
 */
import {
  cancelResultSchema,
  orderDetailSchema,
  orderLiveSchema,
  orderSummarySchema,
  pageResultSchema,
  type CancelOrderRequest,
  type CancelResult,
  type OrderDetail,
  type OrderLive,
  type OrderSummary,
  type PageResult,
} from '@cione/contracts'
import type { HttpClient } from './http'

const orderPageSchema = pageResultSchema(orderSummarySchema)

/**
 * 在途单一页。⚠ 每条是**订单详情**的形状（比列表那行多一个 `domainPayload`），
 * 不是 `OrderSummary` —— 后端 `/ongoing` 每行要读一次子表，这也是它「比 `/live` 重」的原因。
 */
const ongoingPageSchema = pageResultSchema(orderDetailSchema)

export interface OrdersApi {
  /**
   * 我的订单列表。⚠ `status` 传我方状态名（`ORDER_PAID` 等），认不出后端会 400 ——
   *   所以只把已知状态给筛选器，别让用户填自由文本。
   */
  list(params?: {
    status?: string
    page?: number
    size?: number
  }): Promise<PageResult<OrderSummary>>
  /** 详情。⚠ 404 不区分「不存在」与「不是你的」—— 文案写「找不到这个订单」。 */
  detail(orderNo: string): Promise<OrderDetail>
  /**
   * 取消（出票前撤单）。⚠⚠ 返回的 `status` 是**真实新状态**：已付款取消后是
   *   `ORDER_REFUNDING`，不是 `ORDER_CANCELLED`。按它显示，别写死「取消成功 = 已取消」。
   */
  cancel(orderNo: string, body?: CancelOrderRequest): Promise<CancelResult>

  /**
   * 行程实况（S8）。**卡片挂上之后按 `nextPollSeconds` 反复调**，默认 10 秒一次。
   *
   * 🔴 停止条件由**响应**给（`terminal` / `nextPollSeconds === 0`），
   *   判定一律走 contracts 的 `shouldStopLivePolling` / `nextLivePollMs` ——
   *   **别在调用方维护一份「哪些 state 算结束」**（会漂，且漂了没人发现）。
   * ⚠ `orderNo` 必须是**我方**订单号。拿卡片上的 `supplier_order_no` 来调**一律 404**，
   *   而那个 404 与「这单不是你的」是同一个码 —— 会被误判成登录/权限问题。
   * ⚠ 历史单也能调：它回 `terminal: true`，卡片定格成一张行程回顾，不轮询。
   */
  live(orderNo: string): Promise<OrderLive>

  /**
   * 该用户**还没办完**的订单（S8）。页面打开 / 刷新后用它把卡片捞回来。
   *
   * ⚠⚠ **挂载时问一次的接口，别拿它轮询** —— 它比 `/live` 重（每行要读一次子表）。
   * ⚠ `domain` 认不出的后端回 **400**，不会静默忽略 —— 传 `RIDE` / `HOTEL` / `FLIGHT`。
   */
  ongoing(params: {
    domain: string
    page?: number
    size?: number
  }): Promise<PageResult<OrderDetail>>

  /**
   * 🔴 取消**问价**（S8）。只读，什么都不取消。
   *
   * ★ **打车取消是要收钱的**（按司机已出发多久算），而那笔钱**打车平台直接从用户账上扣**，
   *   我方不经手也退不了。⇒ 取消必须是两步：先问价、弹确认、用户点头之后才 {@link cancel}。
   *   一键取消 = 让用户在不知道要收多少钱的情况下取消。
   *
   * ⚠⚠ 它是 POST 但**不改任何东西**；用 POST 是因为**每调一次就是一次真实的平台调用** ——
   *   **别当 `/live` 那样轮询**。
   * ⚠ 回的是**订单详情的形状**，取消费在 `domainPayload` 里（**那是字符串，要 parse**）——
   *   用 contracts 的 `parseRideCancelQuote`。
   * ⚠ 它**不改状态**，别拿它的返回去更新订单状态。
   */
  cancelQuote(orderNo: string): Promise<OrderDetail>
}

export function createOrdersApi(http: HttpClient): OrdersApi {
  return {
    list(params) {
      return http.request({
        method: 'GET',
        path: '/api/orders',
        query: { status: params?.status, page: params?.page, size: params?.size },
        schema: orderPageSchema,
      })
    },

    detail(orderNo) {
      return http.request({
        method: 'GET',
        path: `/api/orders/${encodeURIComponent(orderNo)}`,
        schema: orderDetailSchema,
      })
    },

    cancel(orderNo, body) {
      return http.request({
        method: 'POST',
        path: `/api/orders/${encodeURIComponent(orderNo)}/cancel`,
        body: body ?? {},
        /*
          🔴🔴 **这个头是必需的，不是优化。**

          trade 的 `TradeOrderService.mutate()` 第一行就是
          `if (isBlank(ctx.idempotencyKey())) return failed(idempotencyKeyMissing())`，
          而那个内网码出网是 `COMMON.INVALID_ARGUMENT`。⇒ 不带它，**取消必然失败**，
          而用户看到的是一句「填写的内容有误，请检查后重试」——
          他没填任何东西，也就无从"检查"。

          ⚠⚠ **打车域的失败最贵**：取消是**先出网、后落状态**的。缺这个头时，
            供应商那边的车已经取消掉了，我方状态却推不动、行程段也不放开 ——
            于是 `/ongoing` 一直把它当在途返回，卡片永远占在页面上，
            而用户每点一次「取消」就往供应商那边多发一次取消。
            2026-09-20 联调当场撞到，两张卡就是这么卡住的。

          ⚠ 键取 `cancel:{orderNo}` —— **同一单永远同一个键**：
            用户连点两下、或网络抖动重发，对服务端都是同一个意图。
            用随机值等于关掉幂等（每次都是新键，永远查不到重复）。
          ⚠ 带上它同时让这条请求变得**可重试**（见 `http.ts::canRetry`）——
            这正是幂等键存在的理由：重发不会取消出第二次费用。
        */
        idempotencyKey: `cancel:${orderNo}`,
        schema: cancelResultSchema,
      })
    },

    live(orderNo) {
      return http.request({
        method: 'GET',
        path: `/api/orders/${encodeURIComponent(orderNo)}/live`,
        schema: orderLiveSchema,
      })
    },

    ongoing(params) {
      return http.request({
        method: 'GET',
        path: '/api/orders/ongoing',
        // ⚠ `domain` 必传：缺了或认不出，后端回 400（刻意不静默当「全部域」）
        query: { domain: params.domain, page: params.page, size: params.size },
        schema: ongoingPageSchema,
      })
    },

    cancelQuote(orderNo) {
      return http.request({
        method: 'POST',
        path: `/api/orders/${encodeURIComponent(orderNo)}/cancel-quote`,
        body: {},
        // ⚠ 回的是**订单详情**的形状，取消费在 `domainPayload`（字符串）里
        schema: orderDetailSchema,
      })
    },
  }
}
